import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar, { SidebarItem } from './Sidebar';
import {
  LayoutDashboard,
  Shield,
  Users,
  Key,
  LogOut,
  UserCircle,
  MessageSquareQuote,
  BookOpen,
  Mail,
  UserPlus,
  Megaphone
} from 'lucide-react';


const DashboardLayout = ({ children, title, roles = null }) => {
  const { user, logout, hasRole, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user has required roles
  if (roles) {
    if (roles.length === 1) {
      if (!hasRole(roles[0])) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
              <p className="text-gray-600">You don't have permission to access this page.</p>
            </div>
          </div>
        );
      }
    } else {
      if (!hasAnyRole(roles)) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
              <p className="text-gray-600">You don't have permission to access this page.</p>
            </div>
          </div>
        );
      }
    }
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="flex bg-gray-50 min-h-screen text-gray-900">
      <Sidebar
        profileName={user?.name || user?.email || "User"}
        profileEmail={user?.email || ""}
      >
        {/* Moderator Menu */}
        {hasRole('MODERATOR') && (
          <SidebarItem
            icon={<Shield size={20} />}
            text="Moderator Dashboard"
            to="/moderator"
            active={isActive('/moderator')}
          />
        )}

        {/* Admin Menu */}
        {hasRole('ADMIN') && (
          <>
            <SidebarItem
              icon={<LayoutDashboard size={20} />}
              text="Admin Dashboard"
              to="/admin"
              active={isActive('/admin') && !location.pathname.includes('/users') && !location.pathname.includes('/roles') && !location.pathname.includes('/reviews')}
            />
            <SidebarItem
              icon={<Users size={20} />}
              text="User Management"
              to="/admin/users"
              active={isActive('/admin/users')}
            />
            <SidebarItem
              icon={<UserPlus size={20} />}
              text="Mentors"
              to="/admin/mentors"
              active={isActive('/admin/mentors')}
            />
            <SidebarItem
              icon={<Key size={20} />}
              text="Role Management"
              to="/admin/roles"
              active={isActive('/admin/roles')}
            />
            <SidebarItem
              icon={<MessageSquareQuote size={20} />}
              text="Reviews"
              to="/admin/reviews"
              active={isActive('/admin/reviews')}
            />
            <SidebarItem
              icon={<BookOpen size={20} />}
              text="Programs"
              to="/admin/programs"
              active={isActive('/admin/programs')}
            />
            <SidebarItem
              icon={<Mail size={20} />}
              text="Contacts"
              to="/admin/contacts"
              active={isActive('/admin/contacts')}
            />
            <SidebarItem
              icon={<LayoutDashboard size={20} />}
              text="Career Chavdi"
              to="/admin/career-chavdi"
              active={isActive('/admin/career-chavdi')}
            />
            <SidebarItem
              icon={<Megaphone size={20} />}
              text="Announcement"
              to="/admin/announcement"
              active={isActive('/admin/announcement')}
            />
          </>

        )}

        {/* Common Menu or Profile Link if needed specifically in sidebar */}
        <SidebarItem
          icon={<UserCircle size={20} />}
          text="Profile"
          to="/profile"
          active={isActive('/profile')}
        />

        <hr className="my-3" />

        <SidebarItem
          icon={<LogOut size={20} />}
          text="Logout"
          onClick={handleLogout}
        />

      </Sidebar>

      {/* Main Content */}
      <div className="flex-1 p-8 ml-16 md:ml-0 overflow-y-auto h-screen">
        {title && (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
